import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";

/**
 * Custom fallback UI for error boundary
 */
function ErrorFallback({ error: unknownError, resetError }: { error: unknown; resetError: () => void }) {
  const error = unknownError instanceof Error ? unknownError : new Error(String(unknownError));
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
      <h1 className="text-[2rem] mb-4">
        Oops! Something went wrong
      </h1>
      <p className="mb-6 text-[#666]">
        We're sorry for the inconvenience. The error has been logged and
        we'll look into it.
      </p>
      <button
        type="button"
        onClick={resetError}
        className="px-6 py-3 text-base bg-[#3b82f6] text-white border-none rounded-md cursor-pointer"
      >
        Try Again
      </button>
      {import.meta.env.DEV && error && (
        <details className="mt-8 text-left max-w-[600px]">
          <summary className="cursor-pointer mb-2">
            Error Details (Development Only)
          </summary>
          <pre className="bg-[#f3f4f6] p-4 rounded-md overflow-auto text-sm">
            {error.message}
            {"\n\n"}
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Error Boundary component using Sentry's built-in ErrorBoundary
 * Automatically captures errors and sends them to Sentry
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      showDialog={false} // Set to true if you want Sentry's user feedback dialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
